import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { History, ClipboardList, MapPin } from "lucide-react";
import { Card, Field, IconButton, PrimaryButton, Screen } from "@/components/kit";
import { Stepper } from "@/components/Stepper";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { TankPicker } from "@/components/drop/TankPicker";
import { DropDetails } from "@/components/drop/DropDetails";
import { ResultCard } from "@/components/drop/ResultCard";
import { SaveSiteModal } from "@/components/drop/SaveSiteModal";
import { VeederScan } from "@/components/VeederScan";
import { SiteSwitcher, clip } from "@/components/drop/SiteSwitcher";
import { TankHistory } from "@/components/drop/TankHistory";
import { getPosition } from "@/lib/geo";
import { useSimple } from "@/lib/simple";
import { TrustPanel } from "@/components/TrustPanel";
import { BolScan } from "@/components/drop/BolScan";
import { PrintoutCard } from "@/components/drop/PrintoutCard";
import { DropWarning, ProductTag, WaterWarning, WrongProduct } from "@/components/drop/DropWarning";
import { LogConfirm, OverflowBlock } from "@/components/drop/LogConfirm";
import { dropSafety, isOver90, shownLevel } from "@/lib/safety";
import { EpaCheck } from "@/components/drop/EpaCheck";
import { StickPhoto } from "@/components/StickPhoto";
import { queueDrop } from "@/lib/offline";
import { ShortDisclaimer } from "@/components/Disclaimer";
import { useSession } from "@/context/SessionContext";
import { api, apiError } from "@/lib/api";
import { formatGal, PRODUCTS, productName, scanActive, tankKey, tankLabel, TANK_PRESETS } from "@/lib/tank";

const tankOf = (s, label) => ({ label, capacity: s.capacity, diameter: s.diameter, ends: s.ends, startInches: s.startInches, product: s.product });

export default function DropPage() {
  const { session: s, result: r, patch, ready } = useSession();
  const nav = useNavigate();
  const t = useT();
  const { user } = useAuth();
  const [custom, setCustom] = useState(false);
  const [saving, setSaving] = useState(null);
  const [flash, setFlash] = useState(null);
  const flashTimer = useRef(null);

  useEffect(() => {
    if (ready) setCustom(!TANK_PRESETS.some((x) => tankKey(x) === tankKey(s)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const showFlash = (msg, tone) => {
    clearTimeout(flashTimer.current);
    setFlash({ msg: t(msg), tone });
    flashTimer.current = setTimeout(() => setFlash(null), 3500);
  };
  const failed = (e) => {
    const code = e?.response?.status;
    if (code === 401) { nav("/login?next=/drop"); return; }
    showFlash(code ? `${t("Couldn't save.")} (${code}${apiError(e) ? `: ${apiError(e)}` : ""})` : "Couldn't save. Check your signal and try again.", "critical");
  };
  const needLogin = () => { if (user) return false; nav("/login?next=/drop"); return true; };

  const openSave = async () => {
    if (needLogin()) return;
    try { setSaving(await api.getSites()); } catch { setSaving([]); }
  };

  const simple = useSimple();
  const onSiteTank = !!s.activeSiteId && s.activeKey === tankKey(s);
  const [epaBlock, setEpaBlock] = useState(false);
  const onEpaBlock = useCallback((b) => setEpaBlock(b), []);
  const sf = dropSafety(s, r);
  const [confirming, setConfirming] = useState(false);
  const [spillClosed, setSpillClosed] = useState(false);
  const gallonsRef = useRef(null);
  const [tankProduct, setTankProduct] = useState(null);
  useEffect(() => {
    setTankProduct(null);
    if (!onSiteTank || !user) return;
    api.getSites().then((list) => setTankProduct(list.find((x) => x.id === s.activeSiteId)?.tanks.find((x) => x.label === s.activeLabel)?.product || null)).catch(() => {});
  }, [onSiteTank, user, s.activeSiteId, s.activeLabel]);
  const printed = scanActive(s) && PRODUCTS.some((p) => p.id === s.scan.product) ? s.scan.product : null;
  const expected = tankProduct || printed;
  const expectedFrom = tankProduct ? "saved" : "printout";
  const rawTank = (onSiteTank ? s.activeLabel : s.scan?.label) || "";
  const tankName = tankLabel(rawTank) || t("this tank");
  const mismatch = !!expected && expected !== s.product;
  const mismatchText = mismatch && (expectedFrom === "printout"
    ? t("Printout says {a} in {n}, you're dropping {b}.", { a: t(productName(expected)), n: tankName, b: t(productName(s.product)) })
    : t("{n} is saved as {a}, you're dropping {b}.", { a: t(productName(expected)), n: tankName, b: t(productName(s.product)) }));
  const match = (
    <div className={`product-match ${mismatch ? "product-match-bad" : ""}`} data-testid="product-match">
      {t("Dropping")} <ProductTag id={s.product} testid="match-load-product" />
      {(onSiteTank || printed) && <> {t("into {n}", { n: tankName })} {expected ? <ProductTag id={expected} testid="match-tank-product" /> : <span className="card-sub">{t("(no product saved on this tank)")}</span>}</>}
    </div>
  );
  const editGallons = () => { setSpillClosed(true); setTimeout(() => gallonsRef.current?.querySelector("input")?.focus(), 50); };
  useEffect(() => {
    if (sf.level === "overflow") return;
    const id = setTimeout(() => setSpillClosed(false), 1500);
    return () => clearTimeout(id);
  }, [sf.level]);
  const logDrop = async (confirmed = false) => {
    if (sf.level === "overflow") return;
    if (onSiteTank && epaBlock) { showFlash("Check the EPA warning above and confirm the tank is in service first.", "critical"); return; }
    if ((isOver90(sf.level) || mismatch) && confirmed !== true) { setConfirming(true); return; }
    setConfirming(false);
    if (needLogin()) return;
    const body = {
      ...(onSiteTank ? { name: clip(s.activeName, 80) || null, siteId: s.activeSiteId, tankLabel: s.activeLabel ?? null } : {}),
      capacity: s.capacity, diameter: s.diameter, ends: s.ends, product: s.product, startInches: s.startInches,
      dropGallons: s.dropGallons, finalInches: r.finalInches, finalGallons: r.finalGallons, finalPct: r.finalPct, status: sf.level === "ok" ? "safe" : sf.level === "over90" ? "caution" : "critical",
      at: new Date().toISOString(), usedChart: r.source === "chart", over90: isOver90(sf.level),
      restickConfirmed: isOver90(sf.level) && confirmed === true, productMismatch: mismatch, expectedProduct: mismatch ? expected : null, productConfirmed: mismatch && confirmed === true,
    };
    try {
      await api.addDrop(body);
      showFlash("Drop logged", r.status);
      window.dispatchEvent(new Event("drops-changed"));
      if (onSiteTank) window.dispatchEvent(new Event("sites-changed"));
    } catch (e) {
      if (!e.response) { queueDrop(body); showFlash("No signal. Drop saved on this phone and will upload when you have signal.", "caution"); return; }
      if (e.response.status === 401) queueDrop(body);
      failed(e);
    }
  };

  const confirmSave = async ({ mode, name, siteId, label }) => {
    try {
      let saved;
      const site = mode === "new" ? null : saving.find((x) => x.id === siteId);
      const pos = !site || site.lat == null ? await getPosition(5000) : null;
      const where = pos || (site ? { lat: site.lat, lng: site.lng } : {});
      if (mode === "new") saved = await api.addSite({ name, tanks: [tankOf(s, label)], ...where });
      else {
        const same = site.tanks.find((x) => x.label.toLowerCase() === label.toLowerCase());
        const others = site.tanks.filter((x) => x !== same);
        if (!same && site.tanks.length >= 12) { setSaving(null); showFlash("This station already has 12 tanks. Remove one first.", "critical"); return; }
        const keepChart = { ...(same?.chart && tankKey(same) === tankKey(s) ? { chart: same.chart } : {}), ...(same?.epaTankId ? { epaTankId: same.epaTankId } : {}) };
        saved = await api.updateSite(siteId, { name: site.name, tanks: [...others, { ...tankOf(s, label), ...keepChart }], ...where });
      }
      patch({ activeSiteId: saved.id, activeTank: saved.tanks.length - 1, activeLabel: saved.tanks[saved.tanks.length - 1].label, activeEpa: !!saved.tanks[saved.tanks.length - 1].epaTankId, activeKey: tankKey(s), activeName: clip(`${saved.name} · ${tankLabel(label)}`, 80) });
      window.dispatchEvent(new Event("sites-changed"));
      setSaving(null);
      showFlash(mode === "new" ? "Site saved" : "Tank added to site", "safe");
    } catch (e) { failed(e); }
  };

  return (
    <Screen testID="drop-screen" title={simple ? t("Plan a drop") : t("Drop predictor")} subtitle={simple ? t("Will it fit? See the stick after you unload") : t("The stick after you unload")}
      right={simple ? null : <IconButton testID="open-sites" label={t("Sites")} onPress={() => nav("/sites")}><History size={20} /></IconButton>}>
      <ResultCard s={s} r={r} sf={shownLevel(sf, mismatch)} mismatchText={mismatchText} />
      <PrintoutCard s={s} r={r} patch={patch} />
      <TrustPanel s={s} r={r} showDrop />
      <ShortDisclaimer testID="drop-disclaimer" />
      {onSiteTank && <EpaCheck s={s} t={t} onBlock={onEpaBlock} />}
      {s.dropGallons > 0 && match}
      <div className="action-row">
        <PrimaryButton testID="save-site" small variant="secondary-btn" onPress={openSave}><MapPin size={16} />{t("Save site")}</PrimaryButton>
        <PrimaryButton testID="log-drop" small onPress={() => logDrop()} disabled={sf.level === "overflow"}><ClipboardList size={16} />{t("Log drop")}</PrimaryButton>
      </div>
      {flash && <div className={`flash flash-${flash.tone}`} data-testid="drop-flash">{flash.msg}</div>}
      <SiteSwitcher s={s} patch={patch} onTank={(p) => setCustom(!TANK_PRESETS.some((x) => tankKey(x) === tankKey(p)))} />
      {onSiteTank && user && <TankHistory s={s} />}
      <TankPicker s={s} patch={patch} custom={custom} setCustom={setCustom} />
      <VeederScan compact onApply={(plan) => { patch(plan.patch); if (plan.patch.capacity) setCustom(!TANK_PRESETS.some((x) => tankKey(x) === tankKey({ ...s, ...plan.patch }))); }} />
      <Card testID="inputs-card">
        <div className="inputs inputs-tight">
          <Field label={t("Stick now")}>
            <Stepper blank testID="start-inches" unit="in" decimal step={0.125} max={s.diameter} value={s.startInches} onValue={(v) => patch({ startInches: v })} />
            <StickPhoto testid="start-inches" max={s.diameter} onValue={(v) => patch({ startInches: v })} />
            <span className="field-hint mono" data-testid="start-gallons">{s.startInches > 0 ? `${formatGal(r.startGallons)} gal` : ""}</span>
          </Field>
          <Field label={t("Gallons to drop")}>
            <div ref={gallonsRef} className={isOver90(sf.level) ? "field-danger" : ""} data-testid="drop-gallons-wrap">
              <Stepper blank testID="drop-gallons" unit="gal" step={100} max={40000} value={s.dropGallons} onValue={(v) => patch({ dropGallons: v })} />
            </div>
            <div className="room90-big" data-testid="room90-big"><span className="label">{t("Room to 90%")}</span><b className="mono">{formatGal(sf.room90)} gal</b></div>
          </Field>
        </div>
        <DropWarning sf={shownLevel(sf, mismatch)} mismatchText={mismatchText} testid="gallons-warning" />
        <WaterWarning s={s} testid="gallons-water-warning" />
        {mismatch && s.dropGallons > 0 && isOver90(sf.level) && <WrongProduct text={mismatchText} testid="gallons-product-warning" />}
        {sf.room90 > 0 && sf.room90 !== Math.round(s.dropGallons) && (
          <button type="button" className="ghost-btn max-safe-btn" onClick={() => patch({ dropGallons: sf.room90 })} data-testid="use-max-safe">{t("Use max safe drop ({g} gal)", { g: formatGal(sf.room90) })}</button>
        )}
        <BolScan s={s} patch={patch} />
      </Card>
      <DropDetails s={s} patch={patch} result={r} />
      {confirming && <LogConfirm sf={sf} match={match} needRestick={isOver90(sf.level)} mismatchText={mismatch ? mismatchText : null} onCancel={() => setConfirming(false)} onLog={() => logDrop(true)} />}
      {sf.level === "overflow" && !spillClosed && <OverflowBlock sf={sf} onEdit={editGallons} onClose={() => setSpillClosed(true)} mismatchText={mismatch ? mismatchText : null} />}
      {saving && <SaveSiteModal s={s} sites={saving} onCancel={() => setSaving(null)} onSave={confirmSave} />}
    </Screen>
  );
}
